import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { ScanIcon } from '../../../components/_WMS/_icons/ScanIcon'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnLocateState, LpnInfo, eStepAsnLocate } from '../types'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useUser } from '../../../hooks/useUser'
import { useSettings } from '../../../hooks/useSettings'
import { eSetting } from '../../../enums/eSetting'
import { eTypeSetting } from '../../../enums/eTypeSetting'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { getAsnLpns, registerUserOnLpn } from '../api'
import { Status } from '../../../hooks/useFetchState'
import { eLpnState } from '../../../enums/eLpnState'

interface LpnStepProps extends StepProps<AsnLocateState> {}

export const LpnStep: React.FC<LpnStepProps> = ({
  sharedState,
  setSharedState,
  goToStep
}) => {
  const asn = sharedState.selectedResult ?? sharedState.results[0]
  const [lpnInput, setLpnInput] = useState('')
  const { callApi, state } = useApi()
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const {
    userDataState: { userData },
    getUserData
  } = useUser()
  const { settingsState, getSettings } = useSettings()
  const productOriginId = asn?.productOriginId ?? null
  const [blockByUser, setBlockByUser] = useState(false)
  const blockByUserRef = React.useRef(false)

  useEffect(() => {
    if (!userData) getUserData()
  }, [])

  useEffect(() => {
    setSharedState((prev) => ({ ...prev, stepTitle: 'Ingresa o selecciona una LPN' }))
    return () => {
      setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
    }
  }, [])

  useEffect(() => {
    getSettings(eTypeSetting.Recepcion, productOriginId)
  }, [productOriginId])

  //cuando esta desactivada, la LPN no puede ser trabajada por otro usuario
  useEffect(() => {
    const config = settingsState.settings.find(
      (s) => s.settingsMetadataId === eSetting.InboundAllowTwoUserSameLPN
    )
    if (config !== undefined) {
      const val = config.value === 'false'
      setBlockByUser(val)
      blockByUserRef.current = val
    }
  }, [settingsState.settings])

  useEffect(() => {
    if (!asn?.asnId) return
    callApi(getAsnLpns(asn.asnId))
      .then((response: LpnInfo[]) => {
        setSharedState((prev) => ({
          ...prev,
          lpnList: response,
          lpnAsnId: asn.asnId
        }))
        // Si es solo una LPN, valida si está bloqueada por otro usuario antes de avanzar
        if (response.length === 1 && !sharedState.selectedLpn) {

          const singleLpn = response[0]
          const blockedByOther =
            singleLpn.lpnStateId === eLpnState.Pending &&
            blockByUserRef.current &&
            singleLpn.userId != null &&
            singleLpn.userId !== userData?.userId

            //si no esta bloqueado por otro usuario
          if (!blockedByOther) {
            setSharedState((prev) => ({ ...prev, selectedLpn: singleLpn.lpn }))
            //actualiza al usuario del detalle y avanza de pantalla
            if (userData?.userId) {
              registerUserOnLpn(asn.asnId, singleLpn.lpn, userData.userId)
                .then(() => goToStep(eStepAsnLocate.LocateStepV2))
                .catch(() => openSnackBar('Error al registrar usuario en LPN', false))
            } else {
              goToStep(eStepAsnLocate.LocateStepV2)
            }
          }
          // Si está bloqueada, se queda en la pantalla de selección mostrando el estado
        }
      })
      .catch(() => openSnackBar('Error al obtener LPNs', false))
    return () => {
      setSharedState((prev) => ({ ...prev, lpnAsnId: null, lpnList: [] }))
    }
  }, [asn?.asnId])

  const lpns = sharedState.lpnList
  const isLoading = state.status === Status.Loading

  const isBlockedByOtherUser = (lpn: LpnInfo) =>
    lpn.lpnStateId === eLpnState.Pending &&
    blockByUser && lpn.userId != null && lpn.userId !== userData?.userId

  const hasLpnBlockedByOtherUser = lpns.some(isBlockedByOtherUser)

  const isFinished = (lpn: LpnInfo) => lpn.lpnStateId !== eLpnState.Pending

  const isDisponible = (lpn: LpnInfo) =>
    lpn.lpnStateId === eLpnState.Pending && !isBlockedByOtherUser(lpn)

  const handleLpnSelect = (lpn: LpnInfo) => {
    if (!isDisponible(lpn)) return
    if (!userData?.userId) {
      openSnackBar(
        'No se pudo obtener el usuario. Por favor recarga la página.',
        false
      )
      getUserData()
      return
    }
    registerUserOnLpn(asn.asnId, lpn.lpn, userData.userId)
      .then(() => {
        setSharedState((prev) => ({ ...prev, selectedLpn: lpn.lpn }))
        goToStep(4)
      })
      .catch(() => openSnackBar('Error al registrar usuario en LPN', false))
  }

  const getLpnStyle = (lpn: LpnInfo) => {
    if (lpn.lpnStateId === eLpnState.Manage) return { bg: '#fffde7', color: '#7c6800' }
    if (isFinished(lpn)) return { bg: '#e8f5e9', color: '#388e3c' }
    if (isBlockedByOtherUser(lpn)) return { bg: '#f0f0f0', color: '#555' }
    if (isDisponible(lpn)) return { bg: 'primary.main', color: '#fff' }
    return { bg: '#f0f0f0', color: '#555' }
  }

  const LpnStatusBadge = ({ lpn }: { lpn: LpnInfo }) => {
    if (lpn.lpnStateId === eLpnState.Manage) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
          <Typography variant='body2' sx={{ color: '#7c6800', fontWeight: 600 }}>
            Gestionada
          </Typography>
        </Box>
      )
    }
    if (isFinished(lpn)) {
      const stateLabel =
        lpn.lpnStateId === eLpnState.Created ? 'Creado' :
        lpn.lpnStateId === eLpnState.Finished ? 'Finalizado' : ''
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircleIcon sx={{ color: '#388e3c', fontSize: 18 }} />
          <Typography variant='body2' sx={{ color: '#388e3c', fontWeight: 600 }}>
            {stateLabel}
          </Typography>
        </Box>
      )
    }
    if (isBlockedByOtherUser(lpn)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LockIcon sx={{ color: '#888', fontSize: 18 }} />
          <Typography variant='body2' sx={{ color: '#555', fontWeight: 600 }}>
            En uso
          </Typography>
        </Box>
      )
    }
    return (
      <Typography variant='body2' sx={{ color: '#fff', fontWeight: 600 }}>
        Disponible
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: 3,
        gap: 2,
        height: '100%'
      }}
    >
      <Chip
        label={`ASN: ${asn?.asnNumber ?? ''}`}
        variant='outlined'
        color='primary'
        style={{ fontSize: '0.8125rem', width: '50%' }}
      />

      <TextField
        fullWidth
        variant='outlined'
        placeholder='Escanea o escribe la LPN'
        value={lpnInput}
        onChange={(e) => setLpnInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || !lpnInput.trim()) return
          const found = lpns.find(
            (l) => l.lpn.toLowerCase() === lpnInput.trim().toLowerCase()
          )
          if (!found) {
            openSnackBar('LPN no encontrada', false)
            return
          }
          handleLpnSelect(found)
        }}
        autoComplete='off'
        autoFocus
        slotProps={{
          input: { endAdornment: <ScanIcon width={36} height={46} /> }
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {hasLpnBlockedByOtherUser && (
        <Box
          sx={{
            backgroundColor: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: '8px',
            px: 2,
            py: 1.2
          }}
        >
          <Typography variant='body2' sx={{ color: '#f57c00' }}>
            Esta ASN está siendo trabajada por otro operador
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowY: 'auto',
          flex: 1
        }}
      >
        {lpns.map((lpn) => {
          const disponible = isDisponible(lpn)
          const { bg, color } = getLpnStyle(lpn)
          return (
            <Box
              key={lpn.lpn}
              onClick={() => handleLpnSelect(lpn)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                cursor: disponible ? 'pointer' : 'default',
                backgroundColor: bg,
                border: lpn.lpnStateId === eLpnState.Manage ? '1px solid #f59e0b' : 'none',
                '&:hover': disponible ? { opacity: 0.9 } : {}
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='body1' fontWeight='bold' sx={{ color }}>
                  {lpn.lpn}
                </Typography>
                <LpnStatusBadge lpn={lpn} />
              </Box>
              {lpn.lpnStateId === eLpnState.Manage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.3 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 13, color: '#7c6800' }} />
                  <Typography variant='caption' sx={{ color: '#7c6800' }}>
                    Para reabrirla, ve a <b>Gestionar ASN</b>
                  </Typography>
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Box>
  )
}
